import React from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Trash2, CheckSquare, Square, Undo2 } from "lucide-react";
import { localizedIdentification } from "@/lib/localizedId";
import { formatDate } from "./formatDate";

/**
 * DuplicateRow — one scan inside a group card.
 *
 *  • Species mode: a left-side checkbox toggles inclusion in the merge,
 *    the trash button on the right also toggles inclusion (flips to
 *    Undo2 when excluded). Dismissed groups render the row read-only.
 *  • Exact-hash mode: no checkbox; the trash button actually deletes
 *    the scan (there's no "keep some" concept for byte-identical
 *    duplicates).
 *
 * The tile shows a thumbnail (tap → detail page), the localized common
 * name, and the created-at date. The first row in a group gets an
 * "OLDEST" pill so users know which one merge would preserve.
 */
export default function DuplicateRow({
  scan,
  gi,
  si,
  mode,
  isDismissed,
  excluded,
  language,
  locale,
  busy,
  onToggleExclude,
  onOpenDetail,
  onDelete,
}) {
  const { t } = useTranslation();
  const id = localizedIdentification(scan, language) || {};
  const name = (id.common_name || "").trim() || t("gallery.card.unknown");

  return (
    <div
      className={`flex items-center gap-3 rounded-2xl p-2 pr-3 transition ${
        excluded ? "bg-parchment/20 opacity-55" : "bg-parchment/40"
      }`}
      data-testid={`dup-item-${gi}-${si}`}
      aria-label={excluded ? t("duplicates.rowExcludedAria", { name }) : undefined}
    >
      {/* Per-row checkbox — only meaningful in species merge mode. Exact-hash
          duplicates don't have a "keep some" concept; user always deletes
          extras via the trash button. Hidden for dismissed groups. */}
      {mode === "species" && !isDismissed && (
        <button
          type="button"
          onClick={onToggleExclude}
          data-testid={`dup-toggle-${gi}-${si}`}
          aria-label={excluded
            ? t("duplicates.includeAria", { name })
            : t("duplicates.excludeAria", { name })}
          aria-pressed={!excluded}
          className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center transition ${
            excluded
              ? "bg-white border border-[#E8E5DF] text-leaf-mist"
              : "bg-leaf text-white"
          }`}
        >
          {excluded
            ? <Square className="w-4 h-4" strokeWidth={1.5} />
            : <CheckSquare className="w-4 h-4" strokeWidth={1.5} />}
        </button>
      )}

      <button
        type="button"
        onClick={onOpenDetail}
        className="shrink-0 w-16 h-16 rounded-xl overflow-hidden bg-parchment"
        aria-label={t("duplicates.openAria", { name })}
        data-testid={`dup-open-${gi}-${si}`}
      >
        <img src={scan.thumb_base64 || scan.image_base64} alt="" className="w-full h-full object-cover" />
      </button>

      <div className="flex-1 min-w-0">
        <p className="text-sm text-leaf-ink font-medium truncate" data-testid={`dup-name-${gi}-${si}`}>{name}</p>
        <p className="text-[11px] text-leaf-mist truncate">
          {formatDate(scan.created_at, locale)}
          {si === 0 && !excluded && (
            <span className="ml-2 rounded-full bg-leaf/10 text-leaf px-2 py-0.5 text-[10px]" data-testid={`dup-oldest-${gi}`}>
              {t("duplicates.oldest")}
            </span>
          )}
          {excluded && (
            <span
              className="ml-2 rounded-full bg-white border border-[#E8E5DF] text-leaf-mist px-2 py-0.5 text-[10px]"
              data-testid={`dup-excluded-badge-${gi}-${si}`}
            >
              {t("duplicates.excludedBadge")}
            </span>
          )}
        </p>
      </div>

      {/* Right-side action — species mode toggles inclusion (matches the
          checkbox); exact-hash mode deletes. Dismissed groups skip the
          action row entirely — the whole card is read-only. */}
      {isDismissed ? null : mode === "species" ? (
        <button
          type="button"
          onClick={onToggleExclude}
          aria-label={excluded
            ? t("duplicates.includeAria", { name })
            : t("duplicates.excludeAria", { name })}
          data-testid={`dup-exclude-${gi}-${si}`}
          className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center border transition ${
            excluded
              ? "bg-white border-[#E8E5DF] text-leaf hover:bg-parchment"
              : "bg-white border-[#E8E5DF] text-rust hover:bg-[#FDECE4]"
          }`}
        >
          {excluded
            ? <Undo2 className="w-4 h-4" strokeWidth={1.5} />
            : <Trash2 className="w-4 h-4" strokeWidth={1.5} />}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onDelete(name)}
          disabled={busy}
          aria-label={t("duplicates.deleteAria", { name })}
          data-testid={`dup-delete-${gi}-${si}`}
          className="shrink-0 w-9 h-9 rounded-full bg-white hover:bg-[#FDECE4] border border-[#E8E5DF] text-rust flex items-center justify-center transition disabled:opacity-60"
        >
          {busy
            ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
            : <Trash2 className="w-4 h-4" strokeWidth={1.5} />}
        </button>
      )}
    </div>
  );
}
